import { useEffect, useState, useMemo } from "react"
import { useNavigate, useParams } from "react-router-dom"
import { useTranslation } from "react-i18next"
import { useSelector, useDispatch } from "react-redux"
import { selectCompanyDetails } from "store/company/reducer"
import { fetchCompanyDetails } from "store/company/actions"
import {
  generateManufacturerPDF,
  generateCustomerPDF,
  generateSupplierPDF,
} from "helpers/storePdfExport"
import { getAppUserById, fetchPaginated } from "helpers/api_helper"
import { GET_PRODUCTS } from "helpers/api_urls"
import { CATEGORY_TREE, PRICE_GROUP_OPTIONS, PRICE_GROUP_SWATCHES } from "constants/catalogData"

// Re-export for view components
export { CATEGORY_TREE, PRICE_GROUP_OPTIONS, PRICE_GROUP_SWATCHES }

export const STORE_LANGUAGES = [
  { code: "en", label: "English", flag: "\u{1F1EC}\u{1F1E7}" },
  { code: "gr", label: "\u0395\u03BB\u03BB\u03B7\u03BD\u03B9\u03BA\u03AC", flag: "\u{1F1EC}\u{1F1F7}" },
  { code: "al", label: "Shqip", flag: "\u{1F1E6}\u{1F1F1}" },
  { code: "de", label: "Deutsch", flag: "\u{1F1E9}\u{1F1EA}" },
  { code: "es", label: "Espa\u00F1ol", flag: "\u{1F1EA}\u{1F1F8}" },
]

export const PARENT_CATEGORIES = [
  { value: "Cabinet", label: "Cabinet" },
  { value: "Accessories", label: "Accessories" },
  { value: "Special", label: "Special" },
  { value: "Charges", label: "Charges" },
]

export const COLOR_OPTIONS = [
  { value: "", label: "All Colors" },
  { value: "Unicolor", label: "Unicolor" },
  { value: "Woodgrain", label: "Woodgrain" },
]

export const PAGE_SIZE = 10
const FETCH_BATCH_SIZE = 500

export const useStoreLogic = () => {
  const { t, i18n } = useTranslation()
  const navigate = useNavigate()
  const { storeId } = useParams()
  const dispatch = useDispatch()
  const companyDetails = useSelector(selectCompanyDetails)

  const [userData, setUserData] = useState(null)
  const [userName, setUserName] = useState("")
  const [loading, setLoading] = useState(true)

  const [products, setProducts] = useState([])
  const [displayProducts, setDisplayProducts] = useState([])
  const [totalProducts, setTotalProducts] = useState(0)
  const [productsLoading, setProductsLoading] = useState(false)
  const [hasSearched, setHasSearched] = useState(false)
  const [currentPage, setCurrentPage] = useState(1)
  const [nextSizeInfo, setNextSizeInfo] = useState(null)

  const [productMode, setProductMode] = useState("Cabinet")

  const [cat2Filter, setCat2Filter] = useState("")
  const [cat3Filter, setCat3Filter] = useState("")
  const [cat4Filter, setCat4Filter] = useState("")
  const [colorFilter, setColorFilter] = useState("")
  const [lengthFilter, setLengthFilter] = useState("")
  const [heightFilter, setHeightFilter] = useState("")
  const [priceGroupFilter, setPriceGroupFilter] = useState("")

  const [swatchModalOpen, setSwatchModalOpen] = useState(false)
  const [selectedSwatch, setSelectedSwatch] = useState(null)

  const cat2Options = useMemo(() => {
    if (!productMode || !CATEGORY_TREE[productMode]) return []
    return Object.keys(CATEGORY_TREE[productMode])
  }, [productMode])

  const cat3Options = useMemo(() => {
    if (!productMode || !cat2Filter) return []
    const sub = CATEGORY_TREE[productMode]?.[cat2Filter]
    if (!sub || typeof sub !== "object" || Array.isArray(sub)) return []
    const keys = Object.keys(sub)
    return keys.length > 0 ? keys : []
  }, [productMode, cat2Filter])

  const cat4Options = useMemo(() => {
    if (!productMode || !cat2Filter || !cat3Filter) return []
    const sub = CATEGORY_TREE[productMode]?.[cat2Filter]?.[cat3Filter]
    if (!sub || !Array.isArray(sub)) return []
    return sub
  }, [productMode, cat2Filter, cat3Filter])

  const showCat3 = cat3Options.length > 0
  const showCat4 = cat4Options.length > 0
  const showColor = productMode === "Cabinet"

  const ALL_PG_CODES = useMemo(() => new Set(PRICE_GROUP_OPTIONS.map(pg => pg.code)), [])
  const availablePriceGroups = useMemo(() => {
    if (!products || products.length === 0) return []
    const found = new Set()
    for (const p of products) {
      try {
        const cats = JSON.parse(p.category || "[]")
        if (Array.isArray(cats)) {
          for (const c of cats) {
            if (ALL_PG_CODES.has(c)) found.add(c)
          }
        }
      } catch { /* skip */ }
    }
    return PRICE_GROUP_OPTIONS.filter(pg => found.has(pg.code))
  }, [products])

  const [cart, setCart] = useState([])

  const [langAnchorEl, setLangAnchorEl] = useState(null)
  const [currentLang, setCurrentLang] = useState(() => {
    return localStorage.getItem("storeLanguage") || "en"
  })

  const [settingsOpen, setSettingsOpen] = useState(false)
  const [profitMargin, setProfitMargin] = useState(() => {
    const saved = localStorage.getItem(`storeSettings_${storeId}`)
    if (saved) {
      try {
        const parsed = JSON.parse(saved)
        return parsed.profitMargin || 0
      } catch { return 0 }
    }
    return 0
  })
  const [tempProfitMargin, setTempProfitMargin] = useState(profitMargin)
  // Quote-level discount (%). Applied to subtotal + RAL + extra charge, before VAT.
  const [quoteDiscount, setQuoteDiscount] = useState(0)
  // Extra flat charge (€) — customer-editable, added on top of the cart before discount + VAT.
  const [extraCharge, setExtraCharge] = useState(0)
  const [expandedCat1, setExpandedCat1] = useState({})

  const toggleCat1 = (cat1Key) => {
    setExpandedCat1(prev => ({ ...prev, [cat1Key]: !prev[cat1Key] }))
  }

  useEffect(() => {
    const savedLang = localStorage.getItem("storeLanguage") || "en"
    i18n.changeLanguage(savedLang)
    setCurrentLang(savedLang)
  }, [])

  useEffect(() => {
    if (storeId) {
      const saved = localStorage.getItem(`storeSettings_${storeId}`)
      if (saved) {
        try {
          const parsed = JSON.parse(saved)
          setProfitMargin(parsed.profitMargin || 0)
          setTempProfitMargin(parsed.profitMargin || 0)
        } catch {
          setProfitMargin(0)
          setTempProfitMargin(0)
        }
      }
    }
  }, [storeId])

  const calculateFinalPrice = (b2bPrice) => {
    const price = parseFloat(b2bPrice) || 0
    if (profitMargin === 0) return price
    return price * (1 + profitMargin / 100)
  }

  const handleOpenSettings = () => {
    setTempProfitMargin(profitMargin)
    setSettingsOpen(true)
  }

  const handleCloseSettings = () => { setSettingsOpen(false) }

  const handleSaveSettings = () => {
    const margin = parseFloat(tempProfitMargin) || 0
    setProfitMargin(margin)
    localStorage.setItem(
      `storeSettings_${storeId}`,
      JSON.stringify({ profitMargin: margin })
    )
    setSettingsOpen(false)
  }

  const buildFilterString = () => {
    const filters = []
    if (productMode) filters.push(`(category like %${productMode}%)`)
    if (cat2Filter) filters.push(`(category like %${cat2Filter}%)`)
    if (cat3Filter) filters.push(`(category like %${cat3Filter}%)`)
    if (cat4Filter) filters.push(`(category like %${cat4Filter}%)`)
    if (colorFilter) filters.push(`(category like %${colorFilter}%)`)
    if (priceGroupFilter) filters.push(`(category like %${priceGroupFilter}%)`)
    if (lengthFilter) {
      const len = parseInt(lengthFilter)
      if (!isNaN(len) && len > 0) filters.push(`(productDepth>=${len})`)
    }
    if (heightFilter) {
      const height = parseInt(heightFilter)
      if (!isNaN(height) && height > 0) filters.push(`(productHeight>=${height})`)
    }
    return filters.length > 0 ? filters.join(" AND ") : ""
  }

  const filterToNextSizeUp = (allProducts, inputLength, inputHeight) => {
    if (allProducts.length === 0) return { filtered: [], info: null }
    let filtered = allProducts
    let info = {}
    if (inputLength) {
      const len = parseInt(inputLength)
      if (!isNaN(len) && len > 0) {
        const minLength = Math.min(
          ...filtered.filter(p => p.productDepth && p.productDepth >= len).map(p => p.productDepth)
        )
        if (isFinite(minLength)) {
          filtered = filtered.filter(p => p.productDepth === minLength)
          info.nextLength = minLength
          info.requestedLength = len
        }
      }
    }
    if (inputHeight) {
      const height = parseInt(inputHeight)
      if (!isNaN(height) && height > 0) {
        const minHeight = Math.min(
          ...filtered.filter(p => p.productHeight && p.productHeight >= height).map(p => p.productHeight)
        )
        if (isFinite(minHeight)) {
          filtered = filtered.filter(p => p.productHeight === minHeight)
          info.nextHeight = minHeight
          info.requestedHeight = height
        }
      }
    }
    return { filtered, info: Object.keys(info).length > 0 ? info : null }
  }

  const searchProducts = async (page = 1) => {
    setProductsLoading(true)
    setHasSearched(true)
    setCurrentPage(page)
    try {
      const filterString = buildFilterString()
      const hasDimensionFilter = lengthFilter || heightFilter
      const fetchLimit = hasDimensionFilter ? FETCH_BATCH_SIZE : PAGE_SIZE
      const fetchOffset = hasDimensionFilter ? 0 : (page - 1) * PAGE_SIZE

      const result = await fetchPaginated(GET_PRODUCTS, {
        limit: fetchLimit,
        offset: fetchOffset,
        filter: filterString || undefined,
        order: "productDepth ASC, productHeight ASC, name ASC",
      })

      let finalProducts = result.data || []
      let finalTotal = result.total || 0
      let sizeInfo = null

      if (hasDimensionFilter && finalProducts.length > 0) {
        const { filtered, info } = filterToNextSizeUp(finalProducts, lengthFilter, heightFilter)
        finalProducts = filtered
        finalTotal = filtered.length
        sizeInfo = info
      }

      setProducts(finalProducts)
      setTotalProducts(finalTotal)
      setNextSizeInfo(sizeInfo)

      if (hasDimensionFilter) {
        const startIndex = (page - 1) * PAGE_SIZE
        const endIndex = startIndex + PAGE_SIZE
        setDisplayProducts(finalProducts.slice(startIndex, endIndex))
      } else {
        setDisplayProducts(finalProducts)
      }
    } catch (error) {
      console.error("Error searching products:", error)
      setProducts([])
      setDisplayProducts([])
      setTotalProducts(0)
      setNextSizeInfo(null)
    } finally {
      setProductsLoading(false)
    }
  }

  const handlePageChange = (event, page) => {
    const hasDimensionFilter = lengthFilter || heightFilter
    if (hasDimensionFilter) {
      setCurrentPage(page)
      const startIndex = (page - 1) * PAGE_SIZE
      const endIndex = startIndex + PAGE_SIZE
      setDisplayProducts(products.slice(startIndex, endIndex))
    } else {
      searchProducts(page)
    }
  }

  const parseCategories = (categoryStr) => {
    if (!categoryStr) return []
    try {
      const parsed = JSON.parse(categoryStr)
      return Array.isArray(parsed) ? parsed : [categoryStr]
    } catch { return [categoryStr] }
  }

  const getProductImage = (productPhotos) => {
    if (!productPhotos) return null
    try {
      const parsed = JSON.parse(productPhotos)
      return Array.isArray(parsed) && parsed.length > 0 ? parsed[0] : null
    } catch { return null }
  }

  const handleLangMenuOpen = (event) => { setLangAnchorEl(event.currentTarget) }
  const handleLangMenuClose = () => { setLangAnchorEl(null) }

  const handleLanguageChange = (langCode) => {
    i18n.changeLanguage(langCode)
    setCurrentLang(langCode)
    localStorage.setItem("storeLanguage", langCode)
    handleLangMenuClose()
  }

  const getCurrentLanguageLabel = () => {
    const lang = STORE_LANGUAGES.find((l) => l.code === currentLang)
    return lang ? lang.flag : "\u{1F310}"
  }

  useEffect(() => {
    if (storeId) fetchUserDetails(storeId)
  }, [storeId])

  const fetchUserDetails = async (dfUserId) => {
    try {
      const user = await getAppUserById(dfUserId)
      if (user) {
        setUserData(user)
        const fullName = user.last_name
          ? `${user.first_name || ""} ${user.last_name}`.trim()
          : user.first_name || user.name || user.email
        setUserName(fullName)
      }
    } catch {
      // User may not exist in the app — non-critical
    } finally {
      setLoading(false)
    }
  }

  const handleLogout = () => {
    localStorage.removeItem("authUser")
    navigate("/login")
  }

  const getCartItemKey = (productId, customDimensions, swatch, unitQuantity, doorModel, customRalCode) => {
    const dimPart = customDimensions
      ? `${customDimensions.length || 0}-${customDimensions.height || 0}`
      : "asis"
    const swatchPart = swatch ? swatch.label.replace(/\s+/g, "_") : "noswatch"
    const modelPart = doorModel ? `-dm${doorModel.label.replace(/\s+/g, "_")}` : ""
    const ralPart = customRalCode ? `-ral${customRalCode.replace(/\s+/g, "_")}` : ""
    const unitPart = unitQuantity ? `-uq${unitQuantity}` : ""
    return `${productId}-${dimPart}-${swatchPart}${modelPart}${ralPart}${unitPart}`
  }

  const isKitchenProduct = (product) => {
    if (product.is_configurable) return true
    try {
      const cats = JSON.parse(product.category || "[]")
      return Array.isArray(cats) && (cats.includes("Cabinet") || cats.includes("KITCHEN"))
    } catch { return false }
  }

  const addToCart = (product, { swatchOverride, doorModelOverride, customRalCode, customDimensionsOverride, kasoma, pomolo, modelCode, variantOverride, customSizeSurcharge, unitData, doorData, hingeData, quantity } = {}) => {
    const addQty = Math.max(1, Number.isFinite(Number(quantity)) ? Number(quantity) : 1)
    const effectiveSwatch = swatchOverride !== undefined ? swatchOverride : selectedSwatch
    if (isKitchenProduct(product) && !effectiveSwatch && swatchOverride === undefined) {
      setSwatchModalOpen(true)
      return
    }
    const hasFilters = lengthFilter || heightFilter
    const filterLength = lengthFilter ? parseInt(lengthFilter) : null
    const filterHeight = heightFilter ? parseInt(heightFilter) : null
    const productDepth = product.productDepth ? parseInt(product.productDepth) : null
    const productHeight = product.productHeight ? parseInt(product.productHeight) : null
    const lengthMatches = !filterLength || filterLength === productDepth
    const heightMatches = !filterHeight || filterHeight === productHeight
    const isExactMatch = !hasFilters || (lengthMatches && heightMatches)

    const customDimensions = customDimensionsOverride || (isExactMatch ? null : { length: filterLength, height: filterHeight })
    const swatchInfo = isKitchenProduct(product) ? effectiveSwatch : null
    const doorModelInfo = isKitchenProduct(product) ? (doorModelOverride || null) : null
    const ralCode = (customRalCode || "").trim() || null
    const cartItemKey = getCartItemKey(product.id, customDimensions, swatchInfo, unitData?.unitQuantity, doorModelInfo, ralCode)

    // Apply variant overrides (specific SKU, price, dimensions from variant selection)
    const hingeSurchargeAmt = hingeData?.surcharge || 0
    const surcharge = (customSizeSurcharge || 0) + hingeSurchargeAmt
    let cartProduct = variantOverride
      ? {
          ...product,
          sku: variantOverride.sku || product.sku,
          salesprice: (parseFloat(variantOverride.salesprice || product.salesprice) || 0) + surcharge,
          b2bsalesprice: (parseFloat(variantOverride.b2bsalesprice || product.b2bsalesprice) || 0) + surcharge,
          productWidth: variantOverride.productWidth || product.productWidth,
          productDepth: variantOverride.productDepth || product.productDepth,
          productHeight: variantOverride.productHeight || product.productHeight,
        }
      : surcharge > 0
        ? {
            ...product,
            salesprice: (parseFloat(product.salesprice) || 0) + surcharge,
            b2bsalesprice: (parseFloat(product.b2bsalesprice) || 0) + surcharge,
          }
        : product

    // Apply unit-based pricing (Meter / CubicM)
    if (unitData?.unit === "Meter" || unitData?.unit === "CubicM") {
      const effectivePrice = unitData.unitRate * unitData.unitQuantity
      cartProduct = {
        ...cartProduct,
        b2bsalesprice: effectivePrice,
        salesprice: effectivePrice,
        unit: unitData.unit,
        unitRate: unitData.unitRate,
        unitQuantity: unitData.unitQuantity,
        ...(unitData.cubicDimensions ? { cubicDimensions: unitData.cubicDimensions } : {}),
      }
    }

    setCart((prevCart) => {
      const existingItem = prevCart.find((item) => item.cartItemKey === cartItemKey)
      if (existingItem) {
        return prevCart.map((item) =>
          item.cartItemKey === cartItemKey ? { ...item, quantity: item.quantity + addQty } : item
        )
      }
      return [
        ...prevCart,
        {
          ...cartProduct,
          quantity: addQty,
          cartItemKey,
          customDimensions,
          swatch: swatchInfo,
          doorModel: doorModelInfo,
          customRalCode: ralCode,
          kasoma,
          pomolo,
          modelCode: modelCode || "",
          customSizeSurcharge: surcharge,
          ...(doorData ? { doorOption: doorData.optionName } : {}),
          ...(hingeData ? { hingeCm: hingeData.cm, hingeSurcharge: hingeSurchargeAmt, hingeDoorCount: hingeData.doorCount } : {}),
        },
      ]
    })
  }

  // Kasoma → core_material mapping
  const KASOMA_TO_CORE = { "Basic": "Unicolor", "Wood-Grain": "Woodgrain" }

  const switchCartKasoma = (newKasoma) => {
    const newCoreMaterial = KASOMA_TO_CORE[newKasoma]
    if (!newCoreMaterial) return

    setCart(prevCart => prevCart.map(item => {
      if (!item.is_configurable) return { ...item, kasoma: newKasoma }

      let variants = []
      try { variants = JSON.parse(item.variants || "[]") } catch { return { ...item, kasoma: newKasoma } }
      if (!Array.isArray(variants) || variants.length === 0) return { ...item, kasoma: newKasoma }

      // Find the current variant by SKU to get its finish_code
      const currentVariant = variants.find(v => v.sku === item.sku)
      const finishCode = currentVariant?.finish_code

      // Current dimensions
      const curW = parseInt(item.productWidth) || 0
      const curH = parseInt(item.productHeight) || 0

      // Find matching variant with new core_material, same finish_code and dimensions
      // Only accept variants that have a price (skip NOPRICE entries)
      const newVariant = variants.find(v =>
        parseInt(v.width) === curW
        && parseInt(v.height) === curH
        && v.core_material === newCoreMaterial
        && (!finishCode || v.finish_code === finishCode)
        && v.b2bsalesprice != null
      )

      if (newVariant) {
        return {
          ...item,
          kasoma: newKasoma,
          sku: newVariant.sku,
          salesprice: newVariant.salesprice,
          b2bsalesprice: newVariant.b2bsalesprice,
        }
      }

      // No priced variant found — keep current SKU/price, just update kasoma label
      return { ...item, kasoma: newKasoma }
    }))
  }

  // Pomolo → handle_style mapping
  const POMOLO_TO_HANDLE = { "GOLA": "GOLA", "OXI GOLA": "HANDLE" }

  const switchCartPomolo = (newPomolo) => {
    const newHandleStyle = POMOLO_TO_HANDLE[newPomolo]
    if (!newHandleStyle) return

    setCart(prevCart => prevCart.map(item => {
      if (!item.is_configurable) return { ...item, pomolo: newPomolo }

      let variants = []
      try { variants = JSON.parse(item.variants || "[]") } catch { return { ...item, pomolo: newPomolo } }
      if (!Array.isArray(variants) || variants.length === 0) return { ...item, pomolo: newPomolo }

      const currentVariant = variants.find(v => v.sku === item.sku)
      const finishCode = currentVariant?.finish_code
      const coreMaterial = currentVariant?.core_material
      const curW = parseInt(item.productWidth) || 0

      // Match by width + finish_code + core_material + new handle_style
      // Height changes between GOLA (780) and HANDLE (720), so don't match on height
      // Only accept variants with a price
      const newVariant = variants.find(v =>
        parseInt(v.width) === curW
        && v.handle_style === newHandleStyle
        && (!finishCode || v.finish_code === finishCode)
        && (!coreMaterial || v.core_material === coreMaterial)
        && v.b2bsalesprice != null
      )

      if (newVariant) {
        return {
          ...item,
          pomolo: newPomolo,
          sku: newVariant.sku,
          salesprice: newVariant.salesprice,
          b2bsalesprice: newVariant.b2bsalesprice,
          productHeight: String(newVariant.height),
          customDimensions: item.customDimensions
            ? { ...item.customDimensions, height: parseInt(newVariant.height) }
            : item.customDimensions,
        }
      }

      return { ...item, pomolo: newPomolo }
    }))
  }

  const switchCartModel = (newFinishCode) => {
    if (!newFinishCode) return

    setCart(prevCart => prevCart.map(item => {
      if (!item.is_configurable) return item

      let variants = []
      try { variants = JSON.parse(item.variants || "[]") } catch { return item }
      if (!Array.isArray(variants) || variants.length === 0) return item

      const currentVariant = variants.find(v => v.sku === item.sku)
      const coreMaterial = currentVariant?.core_material
      const handleStyle = currentVariant?.handle_style
      const curW = parseInt(item.productWidth) || 0
      const curH = parseInt(item.productHeight) || 0

      // Match by width + height + core_material + handle_style + new finish_code
      // Only accept variants with a price
      const newVariant = variants.find(v =>
        parseInt(v.width) === curW
        && parseInt(v.height) === curH
        && v.finish_code === newFinishCode
        && (!coreMaterial || v.core_material === coreMaterial)
        && (!handleStyle || v.handle_style === handleStyle)
        && v.b2bsalesprice != null
      )

      if (newVariant) {
        return {
          ...item,
          sku: newVariant.sku,
          salesprice: newVariant.salesprice,
          b2bsalesprice: newVariant.b2bsalesprice,
        }
      }

      return item
    }))
  }

  const switchCartSwatch = (newSwatch) => {
    if (!newSwatch) return
    setCart(prevCart => prevCart.map(item => {
      if (!item.is_configurable) return item
      return { ...item, swatch: newSwatch }
    }))
  }

  const updateItemSwatch = (oldCartItemKey, newSwatch) => {
    if (!newSwatch) return
    setCart(prevCart => {
      const item = prevCart.find(i => i.cartItemKey === oldCartItemKey)
      if (!item) return prevCart
      const newKey = getCartItemKey(item.id, item.customDimensions, newSwatch)
      const existing = prevCart.find(i => i.cartItemKey === newKey)
      if (existing) {
        // Merge: add quantity to existing, remove old
        return prevCart
          .map(i => i.cartItemKey === newKey ? { ...i, quantity: i.quantity + item.quantity } : i)
          .filter(i => i.cartItemKey !== oldCartItemKey)
      }
      // Update swatch and key
      return prevCart.map(i =>
        i.cartItemKey === oldCartItemKey
          ? { ...i, swatch: newSwatch, cartItemKey: newKey }
          : i
      )
    })
  }

  const replaceCart = (items) => setCart(items || [])

  const reorderCartItems = (orderedKeys) => {
    setCart(prev => {
      const keyOrder = new Map(orderedKeys.map((key, i) => [key, i]))
      const ordered = []
      const unordered = []
      for (const item of prev) {
        if (keyOrder.has(item.cartItemKey)) {
          ordered.push(item)
        } else {
          unordered.push(item)
        }
      }
      ordered.sort((a, b) => keyOrder.get(a.cartItemKey) - keyOrder.get(b.cartItemKey))
      return [...ordered, ...unordered]
    })
  }

  const removeFromCart = (cartItemKey) => {
    setCart((prevCart) => prevCart.filter((item) => item.cartItemKey !== cartItemKey))
  }

  const updateQuantity = (cartItemKey, delta) => {
    setCart((prevCart) =>
      prevCart
        .map((item) =>
          item.cartItemKey === cartItemKey ? { ...item, quantity: Math.max(0, item.quantity + delta) } : item
        )
        .filter((item) => item.quantity > 0)
    )
  }

  const formatCustomDimensions = (customDimensions) => {
    if (!customDimensions) return t("Stock Size")
    const parts = []
    if (customDimensions.length) parts.push(`L: ${customDimensions.length}`)
    if (customDimensions.height) parts.push(`H: ${customDimensions.height}`)
    return parts.length > 0 ? parts.join(" x ") + "mm" : t("Stock Size")
  }

  const clearCart = () => { setCart([]) }

  useEffect(() => {
    if (!companyDetails) dispatch(fetchCompanyDetails())
  }, [])

  const cartSubtotal = useMemo(() => {
    return cart.reduce((total, item) => {
      const b2bPrice = parseFloat(item.b2bsalesprice) || 0
      const finalPrice = calculateFinalPrice(b2bPrice)
      return total + finalPrice * item.quantity
    }, 0)
  }, [cart, profitMargin])

  // ── RAL custom-color surcharge ──────────────────────────────────────────
  // €180 flat per distinct RAL code across the whole quote (boxes + specials
  // share the same bucket). The surcharge represents the paint recipe, not
  // quantity, so two items with the same code are charged once.
  const RAL_SURCHARGE_PER_CODE = 180
  const ralSurcharge = useMemo(() => {
    const codes = new Set()
    for (const item of cart) {
      const code = (item.customRalCode || "").trim()
      if (code) codes.add(code.toUpperCase())
    }
    return {
      codes: [...codes],
      perCode: RAL_SURCHARGE_PER_CODE,
      total: codes.size * RAL_SURCHARGE_PER_CODE,
    }
  }, [cart])

  // cartTotal = raw items + RAL surcharge + extra charge (pre-discount, pre-VAT).
  // Kept as the "gross value" the discount is calculated against.
  const extraChargeAmount = useMemo(() => {
    const n = Number(extraCharge)
    return Number.isFinite(n) && n > 0 ? n : 0
  }, [extraCharge])
  const cartTotal = useMemo(
    () => cartSubtotal + ralSurcharge.total + extraChargeAmount,
    [cartSubtotal, ralSurcharge, extraChargeAmount],
  )

  // ── Quote-level discount + VAT breakdown ────────────────────────────────
  // Discount applies to (subtotal + RAL + extra); VAT applies to the discounted net.
  // vat_rate falls back to 24 (Greek standard) when the company hasn't set one.
  const vatRate = useMemo(() => {
    const raw = companyDetails?.vat_rate
    const n = Number(raw)
    return Number.isFinite(n) && n >= 0 ? n : 24
  }, [companyDetails])
  const discountAmount = useMemo(() => {
    const pct = Number(quoteDiscount) || 0
    return Math.max(0, cartTotal * (pct / 100))
  }, [cartTotal, quoteDiscount])
  const netAmount = useMemo(() => Math.max(0, cartTotal - discountAmount), [cartTotal, discountAmount])
  const vatAmount = useMemo(() => netAmount * (vatRate / 100), [netAmount, vatRate])
  const grandTotal = useMemo(() => netAmount + vatAmount, [netAmount, vatAmount])

  const cartItemCount = useMemo(() => {
    return cart.reduce((count, item) => count + item.quantity, 0)
  }, [cart])

  const groupedCart = useMemo(() => {
    const groups = {}
    cart.forEach((item) => {
      let categories = []
      try {
        categories = JSON.parse(item.category || "[]")
        if (!Array.isArray(categories)) categories = [item.category]
      } catch {
        categories = item.category ? [item.category] : []
      }
      // cat1 = first element, cat2 = second element of the category array
      // Map "KITCHEN" → "Cabinet" for grouping compatibility
      const rawCat1 = categories[0] === "KITCHEN" ? "Cabinet" : categories[0]
      const cat1Key = rawCat1 && PARENT_CATEGORIES.some(pc => pc.value === rawCat1)
        ? rawCat1
        : "Other"
      // For cat2, prefer cabinet_position field (new data), fall back to category array
      const posMap = { BASE: "Base", WALL: "Wall", TALL: "Tall" }
      const cat2Key = (item.cabinet_position && posMap[item.cabinet_position.toUpperCase()])
        || categories[1]
        || "Other"
      if (!groups[cat1Key]) groups[cat1Key] = {}
      if (!groups[cat1Key][cat2Key]) groups[cat1Key][cat2Key] = []
      groups[cat1Key][cat2Key].push(item)
    })

    const calcSubtotal = (items) =>
      items.reduce((sum, item) => {
        const price = calculateFinalPrice(parseFloat(item.b2bsalesprice) || 0)
        return sum + price * item.quantity
      }, 0)

    const cat1Totals = {}
    const cat2Subtotals = {}
    for (const [cat1Key, cat2Groups] of Object.entries(groups)) {
      cat1Totals[cat1Key] = 0
      cat2Subtotals[cat1Key] = {}
      for (const [cat2Key, items] of Object.entries(cat2Groups)) {
        const subtotal = calcSubtotal(items)
        cat2Subtotals[cat1Key][cat2Key] = subtotal
        cat1Totals[cat1Key] += subtotal
      }
    }

    const orderedCat1Keys = PARENT_CATEGORIES
      .map(pc => pc.value)
      .filter(key => groups[key])
    if (groups["Other"]) orderedCat1Keys.push("Other")

    return { groups, cat1Totals, cat2Subtotals, orderedCat1Keys }
  }, [cart, profitMargin])

  // Get current user for PDF exports
  const currentUser = (() => {
    try { return JSON.parse(localStorage.getItem("authUser")) || {} }
    catch { return {} }
  })()

  const getPdfParams = () => ({
    companyDetails,
    groupedCart,
    cart,
    profitMargin,
    cartTotal,
    cartItemCount,
    calculateFinalPrice,
    formatCustomDimensions,
    currentUser,
    t,
  })

  const handleExportManufacturer = () => generateManufacturerPDF(getPdfParams())
  const handleExportCustomer = (extra) => generateCustomerPDF({ ...getPdfParams(), ...extra })
  const handleExportSupplier = () => generateSupplierPDF(getPdfParams())

  const clearFilters = () => {
    setCat2Filter("")
    setCat3Filter("")
    setCat4Filter("")
    setColorFilter("")
    setLengthFilter("")
    setHeightFilter("")
    setPriceGroupFilter("")
    setSelectedSwatch(null)
    setNextSizeInfo(null)
  }

  const switchProductMode = (newMode) => {
    if (newMode !== productMode) {
      setProductMode(newMode)
      clearFilters()
      setHasSearched(false)
      setProducts([])
      setDisplayProducts([])
      setTotalProducts(0)
    }
  }

  const clearFiltersAndReset = () => {
    clearFilters()
    setHasSearched(false)
    setProducts([])
    setDisplayProducts([])
    setTotalProducts(0)
  }

  const handleCat2Change = (value) => {
    setCat2Filter(value)
    setCat3Filter("")
    setCat4Filter("")
  }

  const handleCat3Change = (value) => {
    setCat3Filter(value)
    setCat4Filter("")
  }

  const hasActiveFilters = cat2Filter || cat3Filter || cat4Filter || colorFilter || lengthFilter || heightFilter || priceGroupFilter
  const totalPages = Math.ceil(totalProducts / PAGE_SIZE)

  return {
    t, i18n, navigate, storeId, companyDetails,
    userData, userName, loading,
    products, displayProducts, totalProducts, productsLoading, hasSearched, currentPage, nextSizeInfo,
    productMode, switchProductMode,
    cat2Filter, handleCat2Change, cat3Filter, handleCat3Change, cat4Filter, setCat4Filter,
    colorFilter, setColorFilter, lengthFilter, setLengthFilter, heightFilter, setHeightFilter,
    priceGroupFilter, setPriceGroupFilter,
    cat2Options, cat3Options, cat4Options, showCat3, showCat4, showColor,
    availablePriceGroups, hasActiveFilters, clearFilters, clearFiltersAndReset,
    swatchModalOpen, setSwatchModalOpen, selectedSwatch, setSelectedSwatch,
    searchProducts, handlePageChange, totalPages,
    parseCategories, getProductImage, isKitchenProduct,
    cart, addToCart, removeFromCart, updateQuantity, clearCart, replaceCart, reorderCartItems,
    switchCartKasoma, switchCartPomolo, switchCartModel, switchCartSwatch, updateItemSwatch,
    cartTotal, cartSubtotal, ralSurcharge, cartItemCount, groupedCart, getCartItemKey, formatCustomDimensions,
    quoteDiscount, setQuoteDiscount, discountAmount, netAmount, vatRate, vatAmount, grandTotal,
    extraCharge, setExtraCharge, extraChargeAmount,
    calculateFinalPrice, profitMargin, setProfitMargin,
    langAnchorEl, currentLang, handleLangMenuOpen, handleLangMenuClose, handleLanguageChange, getCurrentLanguageLabel,
    settingsOpen, handleOpenSettings, handleCloseSettings, handleSaveSettings,
    tempProfitMargin, setTempProfitMargin,
    expandedCat1, toggleCat1,
    handleLogout,
    handleExportManufacturer, handleExportCustomer, handleExportSupplier,
  }
}
